/*
 * Kleiner Textsatz für AGB, Datenschutz und Impressum des Registrierportals.
 * Bewusst nur das Nötigste und ohne HTML: `# ` und `## ` sind Überschriften,
 * Zeilen mit `- ` eine Liste, Leerzeilen trennen Absätze, `**fett**` und
 * nackte https-Adressen werden ausgezeichnet. Alles andere bleibt Text, damit
 * aus der Inhaltspflege kein Markup in die Seite gelangt.
 */

import type { ComponentChildren } from 'preact';

export type Block =
  | { type: 'h2'; text: string }
  | { type: 'h3'; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'p'; text: string };

export function parseBlocks(source: string): Block[] {
  const blocks: Block[] = [];
  let para: string[] = [];
  let list: string[] | null = null;
  const flushPara = () => { if (para.length) { blocks.push({ type: 'p', text: para.join('\n') }); para = []; } };
  const flushList = () => { if (list) { blocks.push({ type: 'ul', items: list }); list = null; } };
  for (const raw of source.replace(/\r\n/g, '\n').split('\n')) {
    const line = raw.trimEnd();
    if (line.trim() === '') { flushPara(); flushList(); continue; }
    if (line.startsWith('## ')) { flushPara(); flushList(); blocks.push({ type: 'h3', text: line.slice(3).trim() }); continue; }
    if (line.startsWith('# ')) { flushPara(); flushList(); blocks.push({ type: 'h2', text: line.slice(2).trim() }); continue; }
    if (/^\s*[-*] /.test(line)) { flushPara(); (list ??= []).push(line.replace(/^\s*[-*] /, '')); continue; }
    flushList();
    para.push(line);
  }
  flushPara();
  flushList();
  return blocks;
}

/** Fett und Adressen innerhalb einer Zeile. */
export function inline(text: string): ComponentChildren[] {
  const out: ComponentChildren[] = [];
  const re = /\*\*([^*]+)\*\*|(https:\/\/[^\s)]+)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1] !== undefined) out.push(<strong key={key++}>{m[1]}</strong>);
    else out.push(<a key={key++} href={m[2]} target="_blank" rel="noopener noreferrer">{m[2]}</a>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function RichText({ source }: { source: string }) {
  return (
    <>
      {parseBlocks(source).map((b, i) => {
        switch (b.type) {
          case 'h2': return <h2 key={i}>{inline(b.text)}</h2>;
          case 'h3': return <h3 key={i}>{inline(b.text)}</h3>;
          case 'ul': return <ul key={i}>{b.items.map((it, j) => <li key={j}>{inline(it)}</li>)}</ul>;
          default: return <p key={i}>{b.text.split('\n').flatMap((l, j) => (j === 0 ? inline(l) : [<br key={`br${j}`} />, ...inline(l)]))}</p>;
        }
      })}
    </>
  );
}
